import { observer } from 'mobx-react-lite';
import ViewPanel from '@components/view/view-panel.tsx';
import AnnotationSelector from '@components/data/annotation-selector/annotation-selector.tsx';
import { FullExperience } from '@/schemas/experience-schema.ts';
import { useEffect, useRef, useState } from 'react';
import { autorun } from 'mobx';
import scrollStore from '@store/scroll-store.ts';
import ClassString from '@utils/class-string.ts';
import Icon from '@components/core/icon/icon.tsx';
import { IconEnum } from '@components/core/icon/icon-enum.ts';
import localStorageService from '@services/local-storage-service.ts';
import { LocalStorageKey } from '@/enums/local-storage-types.ts';
import authStore from '@store/auth-store.ts';

interface ExperienceDetailsAnnotationPickerProps {
	experience: FullExperience;
}

const ExperienceDetailsAnnotationPicker = observer(
	function ExperienceDetailsAnnotationPicker(
		props: ExperienceDetailsAnnotationPickerProps
	) {
		const [isSticky, setIsSticky] = useState(false);
		const [isMinimized, setIsMinimized] = useState(
			localStorageService.get(
				LocalStorageKey.ChartAnnotationPickerState,
				authStore.user?.id
			) !== 'false'
		);
		const stickyRef = useRef<HTMLDivElement>(null);

		useEffect(() => {
			const disposer = autorun(() => {
				scrollStore.scrollPosition;
				const boundingClientRect =
					stickyRef.current?.getBoundingClientRect();

				if (!boundingClientRect) {
					return;
				}

				setIsSticky(boundingClientRect.top <= 70);
			});

			return () => disposer();
		}, []);

		return (
			<>
				<div
					className={ClassString({
						static: 'sticky sm:hidden top-9 sm:top-11 w-full z-nav transition-all',
						dynamic: { 'top-11': isMinimized },
					})}
				>
					<div
						className={ClassString({
							static: 'absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-white dark:bg-gray-550 hover:bg-blue-300 dark:hover:bg-blue-300 dark:hover:text-gray-600 pt-1 border border-t-0 border-gray-300 cursor-pointer -z-10 transition-all rounded-b-md w-full max-w-20 h-6 flex justify-center items-center shadow-md',
							dynamic: {
								hidden: !isSticky,
								'bottom-12': isMinimized,
							},
						})}
						onClick={() => {
							if (authStore.user) {
								localStorageService.set(
									LocalStorageKey.ChartAnnotationPickerState,
									`${!isMinimized}`,
									authStore.user.id
								);
							}
							setIsMinimized(!isMinimized);
						}}
					>
						<Icon
							icon={
								isMinimized
									? IconEnum.CHEVRON_DOWN
									: IconEnum.CHEVRON_UP
							}
							size={'1.5rem'}
						/>
					</div>
				</div>

				<ViewPanel
					className={ClassString({
						static: '-top-6 w-full z-nav flex sm:hidden justify-center items-center transition-all',
						dynamic: {
							'rounded-t-none w-[110%] -mx-8': isSticky,
							'-mb-4': isMinimized,
							'sticky -mt-4': !isMinimized,
						},
					})}
					panelRef={stickyRef}
				>
					<div
						className={
							'w-full flex justify-center items-center gap-y-2 gap-x-8 sm:gap-x-12 flex-wrap'
						}
					>
						<div className={'text-lg sm:text-2xl hidden sm:block'}>
							Chart Annotations
						</div>
						<AnnotationSelector experience={props.experience} />
					</div>
				</ViewPanel>
			</>
		);
	}
);

export default ExperienceDetailsAnnotationPicker;
